<template>
  <div class="journey-chat-widget">
    <button class="ai-toggle-btn" :class="{ open }" title="Journey Chat" @click="toggle">
      <svg class="icon-chat" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
      <svg class="icon-close" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
    </button>

    <section class="ai-chat" :class="{ open, resizing }" :style="{ width: width + 'px', height: height + 'px' }">
      <div class="ai-chat-resize" title="Drag to resize" @mousedown="startResize"></div>
      <div class="ai-chat-head">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        <span class="ai-chat-title">Journey Chat</span>
        <button class="ai-chat-close" title="Close" @click="close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
      <div ref="body" class="ai-chat-body">
        <div v-for="(m, i) in messages" :key="i" class="ai-msg" :class="m.role">
          <div class="ai-bubble"><span v-if="m.contextStage" class="ai-context-badge">📍 {{ m.contextStage }}</span>{{ m.text }}<span v-if="m.attachmentName" class="ai-attachment-badge">📎 {{ m.attachmentName }}</span><span v-if="m.hasJson" class="ai-json-badge">Journey updated</span></div>
        </div>
        <div v-if="loading" class="ai-msg assistant">
          <div class="ai-bubble ai-bubble-loading">
            <span class="ai-dot"></span><span class="ai-dot"></span><span class="ai-dot"></span>
          </div>
        </div>
      </div>
      <div v-if="contextStage" class="ai-context-chip">
        <span class="ai-context-icon">📍</span>
        <span class="ai-context-label">{{ contextStage }}</span>
        <button class="ai-context-clear" title="Remove context" @click="clearContext">×</button>
      </div>
      <div v-if="attachment || attaching || attachError" class="ai-context-chip" :class="{ 'ai-context-chip-error': attachError }">
        <span class="ai-context-icon">📎</span>
        <span v-if="attaching" class="ai-context-label">Uploading…</span>
        <span v-else-if="attachError" class="ai-context-label">{{ attachError }}</span>
        <span v-else class="ai-context-label">{{ attachment.fileName }}</span>
        <button class="ai-context-clear" title="Remove attachment" @click="clearAttachment">×</button>
      </div>
      <div class="ai-chat-input">
        <div class="ai-composer">
          <input ref="fileInput" type="file" accept=".json,.xlsx,.xls,.pdf" style="display:none" @change="onFileSelected" />
          <button class="ai-attach-btn" title="Attach JSON, Excel, or PDF" :disabled="loading || attaching" @click="$refs.fileInput.click()">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
          </button>
          <textarea
            ref="textarea"
            v-model="draft"
            rows="1"
            placeholder="Describe changes to this journey..."
            :disabled="loading"
            @keydown.enter.exact.prevent="send"
            @input="autoGrow"
          ></textarea>
          <button class="ai-send-btn" title="Send" :disabled="loading || attaching" @click="send">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import api from '../../../../utils/api'
import { getAccessToken } from '../../../../utils/auth'

export default {
  name: 'journey-chat',
  props: {
    currentJourneyJson: { type: Object, default: null }
  },
  data () {
    return {
      open: false,
      resizing: false,
      width: 380,
      height: 520,
      draft: '',
      loading: false,
      contextStage: null,
      attachment: null,
      attaching: false,
      attachError: null,
      messages: [
        { role: 'assistant', text: '👋 Ask me to create or modify this journey — add stages, rewire branches, or rewrite messages.' }
      ]
    }
  },
  beforeDestroy () {
    if (this._onResizeMove) window.removeEventListener('mousemove', this._onResizeMove)
    if (this._onResizeUp) window.removeEventListener('mouseup', this._onResizeUp)
  },
  methods: {
    toggle () {
      this.open = !this.open
      if (this.open) this.$nextTick(() => this.$refs.textarea.focus())
    },
    close () {
      this.open = false
    },
    openWithContext (stageName) {
      this.contextStage = stageName
      this.open = true
      this.$nextTick(() => this.$refs.textarea && this.$refs.textarea.focus())
    },
    clearContext () {
      this.contextStage = null
    },
    clearAttachment () {
      this.attachment = null
      this.attachError = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    async onFileSelected (e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      this.attachError = null
      this.attachment = null
      this.attaching = true
      try {
        const formData = new FormData()
        formData.append('file', file)
        const response = await api.buildRequest('journey-builder-attachment', 'POST', formData)
        if (response.status >= 400) {
          this.attachError = (response.data && response.data.error) || 'Failed to attach file'
        } else {
          this.attachment = response.data
        }
      } catch (err) {
        this.attachError = 'Failed to attach file'
      } finally {
        this.attaching = false
        if (this.$refs.fileInput) this.$refs.fileInput.value = ''
      }
    },
    messageText (m) {
      return m.contextStage ? `In stage "${m.contextStage}": ${m.text}` : m.text
    },
    autoGrow () {
      const el = this.$refs.textarea
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 100) + 'px'
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const body = this.$refs.body
        if (body) body.scrollTop = body.scrollHeight
      })
    },
    async send () {
      const text = this.draft.trim()
      if (!text || this.loading || this.attaching) return
      const contextStage = this.contextStage
      const attachment = this.attachment
      const userMsg = { role: 'user', text, contextStage, attachmentName: attachment && attachment.fileName }
      this.messages.push(userMsg)
      this.draft = ''
      this.contextStage = null
      this.clearAttachment()
      this.$nextTick(() => { if (this.$refs.textarea) this.$refs.textarea.style.height = 'auto' })
      this.loading = true
      this.scrollToBottom()

      try {
        const history = this.messages.slice(0, -1).filter(m => !m.failed).map(m => ({ role: m.role, text: this.messageText(m) }))
        const hasJourney = this.currentJourneyJson && this.currentJourneyJson.stages

        const host = window.location.hostname + (window.location.hostname === 'localhost' ? ':8080' : '')
        const response = await fetch(`${window.location.protocol}//${host}/api/journey-builder-chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` },
          body: JSON.stringify({
            message: contextStage ? `In stage "${contextStage}": ${text}` : text,
            history,
            ...(hasJourney && { journeyJson: { config: this.currentJourneyJson } }),
            ...(attachment && { attachment })
          })
        })

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let sseBuffer = ''
        let streamError = null
        let doneReceived = false

        // The chat shows only the loading dots while generating (no partial-text bubble) —
        // keep them up until the message actually reveals, so there's no gap where neither
        // the dots nor the final message/tree are visible.
        const revealMessage = (message, hasJson) => {
          this.loading = false
          this.messages.push({ role: 'assistant', text: message, hasJson })
          this.scrollToBottom()
        }

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          sseBuffer += decoder.decode(value, { stream: true })
          const lines = sseBuffer.split('\n')
          sseBuffer = lines.pop()
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const event = JSON.parse(line.slice(6))
            if (event.type === 'token') {
              // The LLM writes its "message" field (this text) well before it finishes writing
              // the full journeyJson (often 60+ stages) later in the same response — showing
              // these tokens live would read "done, review the tree" while the tree is still
              // being generated. Ignore them and reveal the message only once the full
              // response (and the tree) is actually ready, below.
            } else if (event.type === 'done') {
              doneReceived = true
              const { message, journeyJson } = event
              const hasJson = !!(journeyJson && journeyJson.config && journeyJson.config.stages)
              if (hasJson) this.$emit('journey-generated', journeyJson.config)
              if (hasJson) {
                // CreateNewJourney's flow canvas finishes its own render (drawLines/fitToScreen)
                // a couple of frames + ~120ms after the journey-generated emit above — reveal the
                // "done" chat message on the same delay so it doesn't say "review the tree" before
                // the tree is actually visible.
                requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(() => revealMessage(message, hasJson), 150)))
              } else {
                revealMessage(message, hasJson)
              }
            } else if (event.type === 'error') {
              streamError = event.msg
            }
          }
        }

        if (streamError) throw new Error(streamError)
        // The connection closed without a 'done' (or 'error') event ever arriving —
        // treat as a failure instead of leaving the chat stuck on the loading dots forever.
        if (!doneReceived) throw new Error('Stream ended unexpectedly')
      } catch (err) {
        // Mark both sides of the failed exchange so they're excluded from history on retry —
        // otherwise a failed send (e.g. pasted JSON that blew the output token cap) keeps
        // getting resent on every subsequent message, compounding the same failure.
        userMsg.failed = true
        this.messages.push({ role: 'assistant', text: 'Something went wrong. Please try again.', failed: true })
        this.loading = false
        this.scrollToBottom()
      }
    },
    startResize (e) {
      this.resizing = true
      const startX = e.clientX
      const startY = e.clientY
      const startW = this.width
      const startH = this.height
      document.body.classList.add('resizing-ai-chat')
      this._onResizeMove = (ev) => {
        let newW = startW - (ev.clientX - startX)
        let newH = startH - (ev.clientY - startY)
        newW = Math.max(300, Math.min(window.innerWidth - 48, newW))
        newH = Math.max(320, Math.min(window.innerHeight - 130, newH))
        this.width = newW
        this.height = newH
      }
      this._onResizeUp = () => {
        this.resizing = false
        document.body.classList.remove('resizing-ai-chat')
        window.removeEventListener('mousemove', this._onResizeMove)
        window.removeEventListener('mouseup', this._onResizeUp)
      }
      window.addEventListener('mousemove', this._onResizeMove)
      window.addEventListener('mouseup', this._onResizeUp)
      e.preventDefault()
    }
  }
}
</script>

<style>
body.resizing-ai-chat{cursor:nwse-resize;user-select:none}
</style>

<style scoped>
.ai-toggle-btn{
  position:fixed;bottom:24px;right:24px;width:52px;height:52px;border-radius:50%;
  background:linear-gradient(180deg,var(--accent) 0%,var(--accent-strong) 100%);color:#fff;border:none;
  display:flex;align-items:center;justify-content:center;cursor:pointer;
  box-shadow:0 6px 20px rgba(59,130,246,0.45);z-index:1050;transition:transform .15s;
}
.ai-toggle-btn:hover{transform:scale(1.06)}
.ai-toggle-btn .icon-close{display:none}
.ai-toggle-btn.open .icon-chat{display:none}
.ai-toggle-btn.open .icon-close{display:block}

.ai-chat{
  position:fixed;bottom:92px;right:24px;
  background:var(--card);border:1px solid var(--card-border);border-radius:14px;
  display:flex;flex-direction:column;overflow:hidden;
  box-shadow:0 16px 48px rgba(0,0,0,0.18);z-index:1040;
  transform:translateY(16px) scale(.97);opacity:0;pointer-events:none;
  transition:opacity .16s ease,transform .16s ease;
}
.ai-chat.open{transform:translateY(0) scale(1);opacity:1;pointer-events:auto}
.ai-chat.resizing{transition:none}
.ai-chat-resize{position:absolute;top:0;left:0;width:18px;height:18px;cursor:nwse-resize;z-index:5}
.ai-chat-resize::before{
  content:"";position:absolute;top:6px;left:6px;width:8px;height:8px;
  border-top:2px solid var(--card-border);border-left:2px solid var(--card-border);border-radius:2px 0 0 0;
}
.ai-chat-resize:hover::before{border-color:var(--accent)}
.ai-chat-head{padding:12px 14px;border-bottom:1px solid var(--card-border);display:flex;align-items:center;gap:8px}
.ai-chat-title{font-size:11px;font-weight:600;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.05em}
.ai-chat-close{
  margin-left:auto;width:24px;height:24px;border-radius:6px;background:transparent;border:none;
  color:var(--text-muted);cursor:pointer;display:flex;align-items:center;justify-content:center;
}
.ai-chat-close:hover{background:var(--btn-bg);color:var(--text)}
.ai-chat-body{flex:1;overflow-y:auto;padding:14px;display:flex;flex-direction:column;gap:12px}
.ai-chat-body::-webkit-scrollbar{width:6px}
.ai-chat-body::-webkit-scrollbar-thumb{background:#d1d5db;border-radius:3px}
.ai-msg{max-width:92%}
.ai-msg.user{align-self:flex-end}
.ai-msg.assistant{align-self:flex-start}
.ai-bubble{padding:9px 12px;border-radius:12px;font-size:12.5px;line-height:1.5;white-space:pre-wrap}
.ai-msg.user .ai-bubble{background:linear-gradient(180deg,var(--accent) 0%,var(--accent-strong) 100%);color:#fff;border-bottom-right-radius:4px}
.ai-msg.assistant .ai-bubble{background:var(--card-label-bg);border:1px solid var(--card-border);color:var(--text);border-bottom-left-radius:4px}
.ai-json-badge{display:block;margin-top:6px;font-size:10.5px;font-weight:600;color:var(--accent-light);background:rgba(59,130,246,0.12);border-radius:4px;padding:2px 7px;width:fit-content}
.ai-context-badge{display:block;margin-bottom:5px;font-size:10.5px;font-weight:600;color:#fff;background:rgba(255,255,255,0.22);border-radius:4px;padding:2px 7px;width:fit-content}
.ai-attachment-badge{display:block;margin-top:6px;font-size:10.5px;font-weight:600;color:var(--text-muted);background:rgba(148,163,184,0.18);border-radius:4px;padding:2px 7px;width:fit-content}
.ai-context-chip{
  display:flex;align-items:center;gap:6px;margin:0 12px 8px;padding:6px 10px;
  background:var(--card-label-bg);border:1px solid var(--card-border);border-radius:8px;
  font-size:11.5px;color:var(--text-muted);
}
.ai-context-chip-error{border-color:#ef4444;color:#ef4444}
.ai-context-chip-error .ai-context-label{color:#ef4444}
.ai-context-label{flex:1;font-weight:600;color:var(--text);font-family:'JetBrains Mono',monospace;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ai-context-clear{background:transparent;border:none;color:var(--text-faint);cursor:pointer;font-size:15px;line-height:1;padding:0 2px}
.ai-context-clear:hover{color:var(--text)}
.ai-bubble-loading{display:flex;align-items:center;gap:4px;padding:11px 14px}
.ai-dot{width:6px;height:6px;border-radius:50%;background:var(--text-faint);animation:ai-dot-bounce 1.2s infinite ease-in-out}
.ai-dot:nth-child(2){animation-delay:.2s}
.ai-dot:nth-child(3){animation-delay:.4s}
@keyframes ai-dot-bounce{0%,80%,100%{transform:scale(0.7);opacity:0.5}40%{transform:scale(1);opacity:1}}
.ai-chat-input{padding:10px 12px;border-top:1px solid var(--card-border)}
.ai-composer{
  display:flex;align-items:flex-end;gap:8px;background:var(--card-label-bg);border:1px solid var(--card-border);
  border-radius:10px;padding:6px 6px 6px 12px;transition:all .15s;
}
.ai-composer:focus-within{border-color:var(--accent);box-shadow:0 0 0 3px rgba(59,130,246,0.12)}
.ai-composer textarea{
  flex:1;border:none;outline:none;background:transparent;font-size:12.5px;color:var(--text);
  resize:none;padding:6px 0;max-height:100px;font-family:inherit;
}
.ai-composer textarea::placeholder{color:var(--text-faint)}
.ai-attach-btn{
  width:26px;height:26px;border-radius:6px;background:transparent;color:var(--text-muted);border:none;
  display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;align-self:center;
}
.ai-attach-btn:hover{background:var(--btn-bg);color:var(--text)}
.ai-attach-btn:disabled{color:var(--text-faint);cursor:not-allowed}
.ai-send-btn{
  width:30px;height:30px;border-radius:7px;background:var(--accent);color:#fff;border:none;
  display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;
}
.ai-send-btn:hover{background:var(--accent-strong)}
.ai-send-btn:disabled{background:var(--text-faint);cursor:not-allowed}
.ai-composer textarea:disabled{color:var(--text-faint)}
</style>



// WEBPACK FOOTER //
// JourneyChat.vue?65ef25de